import {
    Box,
    IconButton as JoyIconButton,
    Input,
    Typography,
} from "@mui/joy";

import {
    formatSupplierUnitPrice,
    formatUnitPriceRange,
} from "../../utils/materialPriceDisplay";
import { formatMeasurementUnit } from "../../utils/formatMeasurementUnit";
import type { LaborCardItemProps, LaborFormulaFlag } from "../../types";
import {
    MATERIAL_ROW,
    materialRowDividerSx,
} from "./materialRowLayout";
import { FormulaQuantityIndicator } from "./FormulaQuantityIndicator";

const METRIC_LABEL_HEIGHT = 18;
const UNIT_PRICE_WIDTH = 136;

const quantityStepperSx = {
    display: "inline-flex",
    alignItems: "stretch",
    height: 32,
    borderRadius: "4px",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.surface",
    overflow: "hidden",
} as const;

const quantityStepperButtonSx = {
    minWidth: 32,
    width: 32,
    height: 32,
    minHeight: 32,
    p: 0,
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
    flexShrink: 0,
    color: "text.primary",
    bgcolor: "transparent",
    "--IconButton-radius": "0px",
    "&:hover": {
        bgcolor: "background.level1",
    },
} as const;

function resolveUnitPriceDisplay(item: LaborCardItemProps): string | null {
    const supplierPrice = formatSupplierUnitPrice(item.supplierUnitPrice);
    if (supplierPrice) return supplierPrice;

    if (item.priceRange) {
        return formatUnitPriceRange(item.priceRange.Min, item.priceRange.Max);
    }

    return null;
}

function LaborQuantityField({
    value,
    onChange,
    hasFormula,
    formulaActive,
    quantityOverridden,
    formula,
    onResetToFormula,
}: {
    value: number;
    onChange: (next: number) => void;
    hasFormula?: boolean;
    formulaActive?: boolean;
    quantityOverridden?: boolean;
    formula?: LaborFormulaFlag;
    onResetToFormula?: () => void;
}) {
    const setQuantity = (next: number) => {
        onChange(Math.max(0, next));
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", md: "flex-end" },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "100%",
                    justifyContent: "space-between",
                    height: METRIC_LABEL_HEIGHT,
                    flexShrink: 0,
                }}
            >
                {hasFormula && (
                    <FormulaQuantityIndicator
                        formula={formula}
                        formulaActive={formulaActive}
                        quantityOverridden={quantityOverridden}
                        onResetToFormula={onResetToFormula}
                    />
                )}
            </Box>
            <Box sx={quantityStepperSx}>
                <JoyIconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setQuantity(value - 1)}
                    sx={{
                        ...quantityStepperButtonSx,
                        borderRight: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    −
                </JoyIconButton>
                <Input
                    type="number"
                    size="sm"
                    value={value}
                    slotProps={{
                        input: {
                            min: 0,
                            sx: {
                                textAlign: "center",
                                fontVariantNumeric: "tabular-nums",
                                MozAppearance: "textfield",
                                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                                    {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                    },
                            },
                        },
                    }}
                    onChange={(e) => setQuantity(Number(e.target.value) || 0)}
                    sx={{
                        width: 72,
                        height: 32,
                        px: 0,
                        borderRadius: 0,
                        border: "none",
                        borderRight: "1px solid",
                        borderColor: "divider",
                        boxShadow: "none",
                        "--Input-minHeight": "32px",
                        "--Input-paddingInline": "8px",
                        bgcolor: "transparent",
                        "--Input-focusedHighlight": "transparent",
                        "&:hover": { bgcolor: "transparent" },
                        "&::before": { display: "none" },
                    }}
                />
                <JoyIconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setQuantity(value + 1)}
                    sx={quantityStepperButtonSx}
                >
                    +
                </JoyIconButton>
            </Box>
        </Box>
    );
}

function Metric({
    label,
    value,
    width,
}: {
    label: string;
    value: string;
    width?: number;
}) {
    return (
        <Box
            sx={{
                width: width ?? { md: 80 },
                minWidth: width ?? { md: 80 },
                flexShrink: 0,
                textAlign: { xs: "left", md: "right" },
            }}
        >
            <Typography
                level="body-xs"
                color="neutral"
                sx={{ lineHeight: 1.2, height: METRIC_LABEL_HEIGHT, mb: -0.5 }}
            >
                {label}
            </Typography>
            <Typography
                level="body-sm"
                sx={{
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.3,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

const laborServiceFieldSx = {
    width: "100%",
    minHeight: 32,
    px: 1.25,
    py: 0.625,
    display: "flex",
    alignItems: "center",
    gap: 1,
    bgcolor: "background.surface",
    borderRadius: "4px",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
} as const;

function LaborServiceField({ name }: { name: string }) {
    return (
        <Box sx={laborServiceFieldSx}>
            <Typography
                level="body-sm"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {name}
            </Typography>
        </Box>
    );
}

export type LaborCardProps = {
    item: LaborCardItemProps;
    onQuantityChange?: (quantity: number) => void;
    onResetToFormula?: () => void;
};

export function LaborCard({
    item,
    onQuantityChange,
    onResetToFormula,
}: LaborCardProps) {
    const unitPrice = resolveUnitPriceDisplay(item);
    const showCategory =
        !!item.category && item.category.trim() !== item.name.trim();
    const categoryLabel = showCategory
        ? [item.category, item.supplierName].filter(Boolean).join(" · ")
        : null;

    return (
        <Box
            sx={{
                px: MATERIAL_ROW.px,
                py: 1.25,
                ...materialRowDividerSx,
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 1fr) auto",
                },
                gap: { xs: 1.25, md: 2.5 },
                alignItems: { md: "center" },
                "&:last-of-type": { borderBottom: "none" },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                {categoryLabel && (
                    <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{ mb: 0.25 }}
                    >
                        {categoryLabel}
                    </Typography>
                )}
                <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>
                    {formatMeasurementUnit(item.unit)}
                </Typography>
                <LaborServiceField name={item.name} />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    gap: 2.5,
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                }}
            >
                <LaborQuantityField
                    value={item.quantity}
                    onChange={(next) => onQuantityChange?.(next)}
                    hasFormula={item.hasFormula}
                    formulaActive={item.formulaActive}
                    quantityOverridden={item.quantityOverridden}
                    formula={item.formula}
                    onResetToFormula={onResetToFormula}
                />
                <Metric
                    label="Unit price"
                    value={unitPrice ? `$${unitPrice}` : "—"}
                    width={UNIT_PRICE_WIDTH}
                />
            </Box>
        </Box>
    );
}
